import React, { useEffect, useState } from 'react';
import * as reviewService from '../lib/services/reviewService';
import * as productService from '../lib/services/productService';
import './LeaveReview.css';

const LeaveReview = () => {
  const [formData, setFormData] = useState({
    first_name: '',
    location: '',
    review_comments: '',
    stars: 5,
    reviewed_item: 'all',
  });
  const [submitting, setSubmitting] = useState(false);
  const [productsLoading, setProductsLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const loadProducts = async () => {
      setProductsLoading(true);
      try {
        const result = await productService.getProducts();
        const activeProducts = Array.isArray(result) ? result : [];
        
        // Extract unique categories from active products
        const uniqueCategories = [...new Set(activeProducts.map((p) => p.category).filter(Boolean))].sort();
        setCategories(uniqueCategories);
      } catch (loadError) {
        console.error('Failed to load products for review form:', loadError);
      } finally {
        setProductsLoading(false);
      }
    };

    loadProducts();
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: name === 'stars' ? Number(value) : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setSuccess('');
    setError('');

    try {
      await reviewService.createReview({
        first_name: formData.first_name.trim(),
        location: formData.location.trim(),
        review_comments: formData.review_comments.trim(),
        stars: formData.stars,
        category: formData.reviewed_item === 'all' ? null : formData.reviewed_item,
      });

      setSuccess('Thanks for your feedback! Your review has been submitted.');
      setFormData({
        first_name: '',
        location: '',
        review_comments: '',
        stars: 5,
        reviewed_item: 'all',
      });
    } catch (submitError) {
      setError(submitError.message || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="leave-review-page">
      <section className="leave-review-hero">
        <div className="container">
          <h1>Leave a Review</h1>
          <p>We would love to hear about your experience with MunchUps.</p>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="leave-review-card">
            <form onSubmit={handleSubmit} className="leave-review-form">
              <div className="leave-review-row">
                <div className="leave-review-field">
                  <label htmlFor="first_name">First Name</label>
                  <input
                    id="first_name"
                    name="first_name"
                    type="text"
                    value={formData.first_name}
                    onChange={handleChange}
                    maxLength={100}
                    required
                    placeholder="Your first name"
                  />
                </div>

                <div className="leave-review-field">
                  <label htmlFor="location">Location</label>
                  <input
                    id="location"
                    name="location"
                    type="text"
                    value={formData.location}
                    onChange={handleChange}
                    maxLength={150}
                    required
                    placeholder="City, Area"
                  />
                </div>
              </div>

              <div className="leave-review-field">
                <label htmlFor="reviewed_item">Review For</label>
                <select
                  id="reviewed_item"
                  name="reviewed_item"
                  value={formData.reviewed_item}
                  onChange={handleChange}
                  required
                  disabled={productsLoading}
                >
                  <option value="all">All Categories</option>
                  {categories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>

              <div className="leave-review-field">
                <label htmlFor="stars">Stars</label>
                <select
                  id="stars"
                  name="stars"
                  value={formData.stars}
                  onChange={handleChange}
                  required
                >
                  <option value={5}>5 - Excellent</option>
                  <option value={4}>4 - Very Good</option>
                  <option value={3}>3 - Good</option>
                  <option value={2}>2 - Fair</option>
                  <option value={1}>1 - Poor</option>
                </select>
              </div>

              <div className="leave-review-field">
                <label htmlFor="review_comments">Review Comments</label>
                <textarea
                  id="review_comments"
                  name="review_comments"
                  value={formData.review_comments}
                  onChange={handleChange}
                  rows={5}
                  maxLength={1000}
                  required
                  placeholder="Tell us what you liked (or how we can improve)."
                />
              </div>

              {error && <p className="leave-review-error">{error}</p>}
              {success && <p className="leave-review-success">{success}</p>}

              <button className="btn btn-primary" type="submit" disabled={submitting}>
                {submitting ? 'Submitting...' : 'Submit Review'}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LeaveReview;
